import { createContext, Dispatch, SetStateAction } from "react";
import { IDashBoard } from "../common/api_interfaces/board/IDashBoard";
import { IOptionsQueryParams } from "../helpers/paramsAutoFiller";
import { ITag } from "../common/api_interfaces/tag/ITag";
import { ITeamMemberDisplay } from "../common/api_interfaces/project/ITeamDisplay";
import { ITaskFormDisplay } from "../common/api_interfaces/task/ITaskFormDisplay";
import { IUserMinimal } from "../common/api_interfaces/user/IUserMinimal";
import { ITypeAndPriority } from "../common/api_interfaces/task/ITypeAndPriority";


export interface TaskAddEditContextType {
    // Состояния данных формы
    taskName: string;
    taskDesc: string;
    taskOldMarkDesc: string;
    taskData: ITaskFormDisplay | undefined;
    typesPriority: ITypeAndPriority | undefined;
    persons: ITeamMemberDisplay[];
    boards: IDashBoard[] | undefined;
    boardId: number | undefined;
    boardName: string | undefined;
    responsible: IUserMinimal | null | undefined;
    type: number | undefined;
    priority: number | undefined;
    tags: ITag[];
    percentageCompletion: number;
    customHours: number | undefined;
    customMinutes: number | undefined;
    startDate: Date | undefined;
    endDate: Date | undefined;
    group: IUserMinimal[];
    value: string;
    editValue: string;
    fileCreate: number[];

    // Состояния UI
    isLoading: boolean;
    showResponsibleSelector: boolean;
    scrollEnded: boolean;
    tab: number;
    showGroupSelector: boolean;
    timeSelectVariant: number | undefined;
    openConfirmWindow: boolean;
    openBoardsSelect: boolean;
    openOldMark: boolean;
    showWarning: boolean;
    forceLinkTerm: boolean;
    forceLinkName: boolean;
    forceLinkDescription: boolean;
    forceLinkExecutor: boolean;

    // Ошибки валидации
    nameError: string | undefined;
    datesError: string | undefined;
    priorityError: string | undefined;
    boardError: string | undefined;
    responsibleError: string | undefined;
    typeError: string | undefined;
    descError: string | undefined;

    // Setter-функции
    setTaskName: Dispatch<SetStateAction<string>>;
    setTaskDesc: Dispatch<SetStateAction<string>>;
    setTaskOldMarkDesc: Dispatch<SetStateAction<string>>;
    setTaskData: Dispatch<SetStateAction<ITaskFormDisplay | undefined>>;
    setTypesPriority: Dispatch<SetStateAction<ITypeAndPriority | undefined>>;
    setShowResponsibleSelector: Dispatch<SetStateAction<boolean>>;
    setScrollEnded: Dispatch<SetStateAction<boolean>>;
    setPersons: Dispatch<SetStateAction<ITeamMemberDisplay[]>>;
    setBoards: Dispatch<SetStateAction<IDashBoard[] | undefined>>;
    setBoardName: Dispatch<SetStateAction<string | undefined>>;
    setResponsible: Dispatch<SetStateAction<IUserMinimal | null | undefined>>;
    setType: Dispatch<SetStateAction<number | undefined>>;
    setPriority: Dispatch<SetStateAction<number | undefined>>;
    setTags: Dispatch<SetStateAction<ITag[]>>;
    setPercentageCompletion: Dispatch<SetStateAction<number>>;
    setTab: Dispatch<SetStateAction<number>>;
    setCustomHours: Dispatch<SetStateAction<number | undefined>>;
    setCustomMinutes: Dispatch<SetStateAction<number | undefined>>;
    setShowGroupSelector: Dispatch<SetStateAction<boolean>>;
    setStartDate: Dispatch<SetStateAction<Date | undefined>>;
    setEndDate: Dispatch<SetStateAction<Date | undefined>>;
    setGroup: Dispatch<SetStateAction<IUserMinimal[]>>;
    setValue: Dispatch<SetStateAction<string>>;
    setEditValue: Dispatch<SetStateAction<string>>;
    setFileCreate: Dispatch<SetStateAction<number[]>>;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    setTimeSelectVariant: Dispatch<SetStateAction<number | undefined>>;
    setOpenConfirmWindow: Dispatch<SetStateAction<boolean>>;
    setBoardsSelect: Dispatch<SetStateAction<boolean>>;
    setOpenOldMark: Dispatch<SetStateAction<boolean>>;
    setShowWarning: Dispatch<SetStateAction<boolean>>;
    setNameError: Dispatch<SetStateAction<string | undefined>>;
    setDatesError: Dispatch<SetStateAction<string | undefined>>;
    setPriorityError: Dispatch<SetStateAction<string | undefined>>;
    setBoardError: Dispatch<SetStateAction<string | undefined>>;
    setResponsibleError: Dispatch<SetStateAction<string | undefined>>;
    setTypeError: Dispatch<SetStateAction<string | undefined>>;
    setDescError: Dispatch<SetStateAction<string | undefined>>;

    maxDescription: boolean;
    curDescriptionLength: number;
    setCurDescriptionLength: Dispatch<SetStateAction<number>>;
    setMaxDescription: Dispatch<SetStateAction<boolean>>;

    // Функции обработчики
    calculateRequiredTime: () => number;
    filterTags: () => { oldTags: ITag[]; newTags: ITag[] };
    normalizeDescription: (description: string) => string;
    normalizeDate: (date: Date | null | undefined) => Date | undefined;
    compileRequest: () => any;
    compileSaveParam: () => any;
    handleTaskAction: () => Promise<void>;
    renderTaskOptions: (
        items: any[] | undefined,
        selectedId: number | string | undefined | null,
        onSelect: (id: number) => void,
        Component: React.ComponentType<{ name: string; color: string; icon: string }>
    ) => JSX.Element[] | null;
    taskTypeDisplay: () => JSX.Element[] | null;
    taskPriorityDisplay: () => JSX.Element[] | null;
    handleTypeSelection: (typeId: number) => void;
    handlePrioritySelection: (priorityId: number) => void;
    onChangeCustomTime: (event: React.ChangeEvent<HTMLInputElement>) => void;
    selectTimeVariant: (event: React.MouseEvent<HTMLButtonElement>) => void;
    handleGroupSelection: (personId: number) => void;
    onAddTags: (tags: ITag[]) => Promise<void>;
    closeResponsibleSelector: () => void;
    handleResponsibleSelection: (personId: number) => void;
    handleGroupRemove: (personId: number) => void;
    appendPersons: (skip: number, take: number, filters: any) => Promise<void>;
    onChangeStartDate: (e: any) => void;
    onChangeEndDate: (e: any) => void;
    openResponsibleSelector: () => void;
    clearResponsible: () => void;
    onChangeName: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onConfirm: () => void;
    changePage: (id: number) => void;
    closeGroupSelector: () => void;
    loadPersons: (filters: any) => Promise<void>;
    removeTags: (tags: ITag | ITag[]) => Promise<void>;
    openGroupSelector: () => Promise<void>;
    getPriority: () => Promise<ITypeAndPriority | undefined>;
    fetchData: () => Promise<void>;
    closeAndClearTree: () => Promise<void>;
    loadDataStore: () => void;
    fetchInitialData: () => Promise<void>;
    loadTaskData: () => Promise<void>;
    updateTaskFormState: (res: any, isOldTask: boolean) => void;
    checkAndLoadFromParams: () => Promise<void>;
    loadTeamMembers: () => Promise<void>;
    boardIdSave: (id: number, name: string) => void;
    handleSaveTaskDesk: (content: string) => void;
    blockDomCustomAllert: () => JSX.Element;
    openOldView: () => void;
    handleClickOutside: (event: globalThis.MouseEvent) => void;

    wrapperRef: React.RefObject<HTMLDivElement>;
    buttonRef: React.RefObject<HTMLDivElement>;

    mode: "create" | "edit";
    optionsQueryParams: IOptionsQueryParams;
    taskId: number | undefined;
    closeFunc: () => void;
    columnId?: number;
    onTaskAction?: () => void;
    projectId?: number;
    permission?: boolean;
    responsibleRequired: boolean;
    parentTaskId?: number;
}

export const TaskAddEditContext = createContext<TaskAddEditContextType>({
    // Default values for all properties
    forceLinkName: false,
    forceLinkDescription: false,
    forceLinkTerm: false,
    forceLinkExecutor: false,
    taskName: "",
    taskDesc: "",
    taskOldMarkDesc: "",
    taskData: undefined,
    typesPriority: undefined,
    persons: [],
    boards: undefined,
    boardId: undefined,
    boardName: undefined,
    responsible: undefined,
    type: undefined,
    priority: undefined,
    tags: [],
    percentageCompletion: 0,
    customHours: undefined,
    customMinutes: undefined,
    startDate: undefined,
    endDate: undefined,
    group: [],
    value: "",
    editValue: "",
    fileCreate: [],
    isLoading: true,
    showResponsibleSelector: false,
    scrollEnded: false,
    tab: 0,
    showGroupSelector: false,
    timeSelectVariant: undefined,
    openConfirmWindow: false,
    openBoardsSelect: false,
    openOldMark: false,
    showWarning: false,
    nameError: undefined,
    datesError: undefined,
    priorityError: undefined,
    boardError: undefined,
    responsibleError: undefined,
    typeError: undefined,
    descError: undefined,

    maxDescription: true,
    curDescriptionLength: 0,
    setCurDescriptionLength: () => { },
    setMaxDescription: () => { },

    // Default setters (empty functions)
    setTaskName: () => { },
    setTaskDesc: () => { },
    setTaskOldMarkDesc: () => { },
    setTaskData: () => { },
    setTypesPriority: () => { },
    setShowResponsibleSelector: () => { },
    setScrollEnded: () => { },
    setPersons: () => { },
    setBoards: () => { },
    setBoardName: () => { },
    setResponsible: () => { },
    setType: () => { },
    setPriority: () => { },
    setTags: () => { },
    setPercentageCompletion: () => { },
    setTab: () => { },
    setCustomHours: () => { },
    setCustomMinutes: () => { },
    setShowGroupSelector: () => { },
    setStartDate: () => { },
    setEndDate: () => { },
    setGroup: () => { },
    setValue: () => { },
    setEditValue: () => { },
    setFileCreate: () => { },
    setIsLoading: () => { },
    setTimeSelectVariant: () => { },
    setOpenConfirmWindow: () => { },
    setBoardsSelect: () => { },
    setOpenOldMark: () => { },
    setShowWarning: () => { },
    setNameError: () => { },
    setDatesError: () => { },
    setPriorityError: () => { },
    setBoardError: () => { },
    setResponsibleError: () => { },
    setTypeError: () => { },
    setDescError: () => { },

    // Default function implementations
    calculateRequiredTime: () => 0,
    filterTags: () => ({ oldTags: [], newTags: [] }),
    normalizeDescription: (description: string) => description,
    normalizeDate: (date: Date | null | undefined) => date || undefined,
    compileRequest: () => ({}),
    compileSaveParam: () => ({}),
    handleTaskAction: async () => { },
    renderTaskOptions: () => null,
    taskTypeDisplay: () => null,
    taskPriorityDisplay: () => null,
    handleTypeSelection: () => { },
    handlePrioritySelection: () => { },
    onChangeCustomTime: () => { },
    selectTimeVariant: () => { },
    handleGroupSelection: () => { },
    onAddTags: async () => { },
    closeResponsibleSelector: () => { },
    handleResponsibleSelection: () => { },
    handleGroupRemove: () => { },
    appendPersons: async () => { },
    onChangeStartDate: () => { },
    onChangeEndDate: () => { },
    openResponsibleSelector: () => { },
    clearResponsible: () => { },
    onChangeName: () => { },
    onConfirm: () => { },
    changePage: () => { },
    closeGroupSelector: () => { },
    loadPersons: async () => { },
    removeTags: async () => { },
    openGroupSelector: async () => { },
    getPriority: async () => undefined,
    fetchData: async () => { },
    closeAndClearTree: async () => { },
    loadDataStore: () => { },
    fetchInitialData: async () => { },
    loadTaskData: async () => { },
    updateTaskFormState: () => { },
    checkAndLoadFromParams: async () => { },
    loadTeamMembers: async () => { },
    boardIdSave: () => { },
    handleSaveTaskDesk: () => { },
    blockDomCustomAllert: () => <></>,
    openOldView: () => { },
    handleClickOutside: () => { },

    wrapperRef: { current: null },
    buttonRef: { current: null },

    mode: "create",
    optionsQueryParams: {
        page: "project",
        entity: undefined,
        action: undefined,
        entityId: undefined,
    },
    taskId: undefined,
    closeFunc: () => {},
    columnId: -1,
    onTaskAction: () => {},
    projectId: -1,
    permission: false,
    responsibleRequired: false,
    parentTaskId: -1,
});